<template>
    <main class="grouping-appointment">
        <header class="grouping-appointment__header">
            <button type="button" class="icon-button" title="Voltar" @click="back"><ArrowLeftIcon /></button>
            <div v-if="grouping">
                <h1>Apontamento parcial</h1>
                <p>{{ grouping.code }} - {{ grouping.description }}</p>
            </div>
            <span v-if="grouping" :class="['type-pill', grouping.type === 'NESTING' ? 'type-pill--nesting' : '']">
                {{ typeLabel(grouping.type) }}
            </span>
        </header>

        <div v-if="loading" class="state-message"><LoaderIcon class="spin" /> Carregando apontamento</div>
        <div v-else-if="loadError" class="state-message state-message--error"><AlertCircleIcon /> {{ loadError }}</div>

        <template v-else-if="grouping">
            <OrderGroupingTimers class="appointment-timers" :grouping="grouping" />
            <div v-if="!isRunning" class="running-warning">
                <AlertCircleIcon /> O agrupamento precisa estar em Execução para receber apontamentos.
            </div>

            <section v-if="isNesting" class="nesting-production">
                <header>
                    <div>
                        <h2>Apontamento total do Nesting</h2>
                        <p>Informe uma única quantidade produzida para o conjunto.</p>
                    </div>
                    <span>{{ grouping.orders.length }} ordens filhas</span>
                </header>

                <div class="nesting-production__metrics">
                    <div><small>Total planejado</small><strong>{{ formatNumber(grouping.totalPlanned) }}</strong></div>
                    <div><small>Produzido acumulado</small><strong>{{ formatNumber(grouping.totalProduced) }}</strong></div>
                    <div><small>Produzido informado</small><strong>{{ formatNumber(totalProduced) }}</strong></div>
                    <div><small>Saldo após apontamento</small><strong>{{ formatNumber(balanceAfterAppointment) }}</strong></div>
                </div>

                <div class="total-produced-field">
                    <div class="total-produced-field__label">
                        <PackageIcon />
                        <small>Quantidade total produzida do Nesting</small>
                    </div>

                    <div class="production-stepper">
                        <div class="production-stepper__main">
                            <button
                                type="button"
                                class="production-stepper__step-button"
                                title="Diminuir quantidade"
                                :disabled="Number(totalProduced || 0) <= 0"
                                @click="adjustTotalProduced(-1)"
                            >
                                <MinusIcon />
                            </button>

                            <label class="production-stepper__value">
                                <span class="sr-only">Quantidade total produzida do Nesting</span>
                                <input
                                    v-model.number="totalProduced"
                                    type="number"
                                    inputmode="decimal"
                                    min="0"
                                    :max="remainingBalance"
                                    step="0.01"
                                    @blur="normalizeTotalProduced"
                                />
                            </label>

                            <button
                                type="button"
                                class="production-stepper__step-button"
                                title="Aumentar quantidade"
                                :disabled="Number(totalProduced || 0) >= remainingBalance"
                                @click="adjustTotalProduced(1)"
                            >
                                <PlusIcon />
                            </button>
                        </div>

                        <div class="production-stepper__shortcuts">
                            <button
                                v-for="quantity in quickQuantities"
                                :key="quantity"
                                type="button"
                                class="production-stepper__shortcut-button"
                                :disabled="Number(totalProduced || 0) >= remainingBalance"
                                @click="adjustTotalProduced(quantity)"
                            >
                                + {{ quantity }}
                            </button>
                            <button
                                type="button"
                                class="production-stepper__total-button"
                                :disabled="remainingBalance <= 0 || Number(totalProduced || 0) === remainingBalance"
                                @click="setTotalProduced"
                            >
                                <CheckCircleIcon />
                                Preencher saldo total
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section class="appointment-table">
                <header>
                    <div>
                        <h2>{{ isNesting ? "Composição e refugo por Ordem de Produção" : "Apontamento por Ordem de Produção" }}</h2>
                        <p v-if="isNesting">O refugo é individual por OP e exige motivo quando maior que zero.</p>
                        <p v-else>Produção, retrabalho e refugo são lançados individualmente por OP.</p>
                    </div>
                    <span>Recurso: {{ grouping.resourceName }}</span>
                </header>

                <div class="table-scroll">
                    <table :class="{ 'appointment-table__table--nesting': isNesting }">
                        <thead>
                            <tr>
                                <th>Ordem</th>
                                <th>Operação</th>
                                <th>Produto</th>
                                <th class="numeric">Planejado</th>
                                <th v-if="isNesting" class="numeric">Qtd. sugerida</th>
                                <th v-if="!isNesting" class="numeric">Produzido</th>
                                <th v-if="!isNesting" class="numeric">Retrabalho</th>
                                <th class="numeric">Refugo da OP</th>
                                <th>Motivo do refugo</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="order in grouping.orders" :key="order.operationId">
                                <td class="order-cell" data-label="Ordem">
                                    <strong>{{ order.orderNumber }}</strong>
                                    <small v-if="isNesting" class="mobile-suggested-quantity">
                                        Qtd.: {{ formatNumber(forms[order.operationId].producedQuantity) }}
                                    </small>
                                </td>
                                <td class="operation-cell" data-label="Operação">{{ order.operationNumber }} - {{ order.operationName }}</td>
                                <td class="description-cell" data-label="Produto">{{ order.description }}</td>
                                <td class="numeric planned-cell" data-label="Planejado">{{ formatNumber(order.plannedQuantity) }}</td>
                                <td v-if="isNesting" class="numeric suggested-cell" data-label="Qtd. sugerida">
                                    <input
                                        v-model.number="forms[order.operationId].producedQuantity"
                                        class="quantity-input quantity-input--suggested"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        @blur="normalizeSuggestedQuantity(order.operationId)"
                                    />
                                </td>
                                <td v-if="!isNesting" class="numeric produced-cell" data-label="Produzido">
                                    <input v-model.number="forms[order.operationId].producedQuantity" class="quantity-input" type="number" min="0" step="0.01" />
                                </td>
                                <td v-if="!isNesting" class="numeric rework-cell" data-label="Retrabalho">
                                    <input v-model.number="forms[order.operationId].reworkQuantity" class="quantity-input" type="number" min="0" step="0.01" />
                                </td>
                                <td class="numeric refuse-cell" data-label="Refugo da OP">
                                    <input
                                        v-model.number="forms[order.operationId].refuseQuantity"
                                        class="quantity-input"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        @input="clearReasonWhenNoRefuse(order.operationId)"
                                    />
                                </td>
                                <td class="reason-cell" data-label="Motivo do refugo">
                                    <select
                                        v-model="forms[order.operationId].refuseReasonId"
                                        :class="['reason-select', { invalid: reasonMissing(order.operationId) }]"
                                        :disabled="Number(forms[order.operationId].refuseQuantity || 0) <= 0"
                                    >
                                        <option :value="null">{{ Number(forms[order.operationId].refuseQuantity || 0) > 0 ? "Selecione o motivo" : "Sem refugo" }}</option>
                                        <option v-for="reason in getRefuseReasons(order.workcenterId)" :key="reason.id" :value="reason.id">{{ reason.name }}</option>
                                    </select>
                                    <small v-if="reasonMissing(order.operationId)" class="reason-error">Motivo obrigatório</small>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="3" class="totals-title"><strong>Totais do agrupamento</strong></td>
                                <td class="numeric" data-label="Planejado"><strong>{{ formatNumber(grouping.totalPlanned) }}</strong></td>
                                <td v-if="isNesting" :class="['numeric', { 'allocation-mismatch': !nestingAllocationMatchesTotal }]" data-label="Qtd. sugerida">
                                    <strong>{{ formatNumber(nestingSuggestedTotal) }}</strong>
                                </td>
                                <td v-if="!isNesting" class="numeric" data-label="Produzido"><strong>{{ formatNumber(normalProducedTotal) }}</strong></td>
                                <td v-if="!isNesting" class="numeric" data-label="Retrabalho"><strong>{{ formatNumber(normalReworkTotal) }}</strong></td>
                                <td class="numeric" data-label="Refugo"><strong>{{ formatNumber(refuseTotal) }}</strong></td>
                                <td data-label="Motivos">{{ hasMissingReason ? "Existem motivos pendentes" : "Motivos preenchidos" }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </section>

            <footer class="appointment-footer">
                <div v-if="error" class="form-error"><AlertCircleIcon /> {{ error }}</div>
                <button type="button" class="primary-button" :disabled="!canSave || saving" @click="save">
                    <LoaderIcon v-if="saving" class="spin" />
                    <SaveIcon v-else />
                    Salvar apontamento
                </button>
            </footer>
        </template>

        <OrderGroupingActionModal
            :open="sapErrorModalOpen"
            :title="appointmentCreated ? 'Apontamento concluído parcialmente' : 'Apontamento não realizado'"
            eyebrow="Retorno do SAP"
            :description="sapErrorDescription"
            tone="warning"
            confirm-label="Voltar ao agrupamento"
            :cancel-label="appointmentCreated ? 'Fechar' : 'Tentar novamente'"
            @cancel="closeSapErrorModal"
            @confirm="confirmSapErrorModal"
        >
            <AlertTriangleIcon slot="icon" />
            <div class="sap-error-list" role="alert">
                <article v-for="failure in sapErrors" :key="`${failure.operationId}-${failure.orderNumber}`">
                    <strong>{{ failure.orderNumber }} · Op. {{ failure.operationNumber }}</strong>
                    <span>{{ failure.message }}</span>
                </article>
            </div>
        </OrderGroupingActionModal>
    </main>
</template>

<script>
import {
    AlertCircleIcon,
    AlertTriangleIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    LoaderIcon,
    MinusIcon,
    PackageIcon,
    PlusIcon,
    SaveIcon
} from "vue-feather-icons";
import createOrderGroupingApi from "./orderGroupingApi";
import { apiErrorMessage, typeLabel } from "./groupingPresentation";
import OrderGroupingActionModal from "./OrderGroupingActionModal.vue";
import OrderGroupingTimers from "./OrderGroupingTimers.vue";

export default {
    components: {
        AlertCircleIcon,
        AlertTriangleIcon,
        ArrowLeftIcon,
        CheckCircleIcon,
        LoaderIcon,
        MinusIcon,
        OrderGroupingActionModal,
        OrderGroupingTimers,
        PackageIcon,
        PlusIcon,
        SaveIcon
    },
    data() {
        return {
            grouping: null,
            refuseReasons: [],
            forms: {},
            totalProduced: 0,
            quickQuantities: [1, 5, 10, 25, 50, 100],
            loading: false,
            saving: false,
            appointmentRequestId: null,
            sapErrorModalOpen: false,
            sapErrors: [],
            appointmentCreated: false,
            loadError: "",
            error: ""
        };
    },
    computed: {
        siteId() {
            return this.$cookies.get("userInfo")?.currentSite;
        },
        isNesting() {
            return this.grouping?.type === "NESTING";
        },
        isRunning() {
            return ["executando", "running"].includes(
                String(this.grouping?.currentStatusName || "").toLocaleLowerCase("pt-BR")
            );
        },
        remainingBalance() {
            return Math.max(Number(this.grouping.totalPlanned) - Number(this.grouping.totalProduced), 0);
        },
        balanceAfterAppointment() {
            return Math.max(this.remainingBalance - Number(this.totalProduced || 0), 0);
        },
        normalProducedTotal() {
            return this.sumField("producedQuantity");
        },
        nestingSuggestedProduction() {
            if (!this.isNesting || !this.grouping?.orders?.length) return {};

            const totalUnits = Math.round(Math.max(Number(this.totalProduced || 0), 0) * 100);
            const weightedOrders = this.grouping.orders.map((order, index) => ({
                operationId: order.operationId,
                index,
                weight: this.nestingAllocationWeight(order)
            }));
            const totalWeight = weightedOrders.reduce((total, order) => total + order.weight, 0);

            if (totalUnits === 0 || totalWeight <= 0)
                return Object.fromEntries(weightedOrders.map(order => [order.operationId, 0]));

            const allocations = weightedOrders.map(order => {
                const exactUnits = totalUnits * order.weight / totalWeight;
                const baseUnits = Math.floor(exactUnits);
                return {
                    ...order,
                    baseUnits,
                    remainder: exactUnits - baseUnits
                };
            });
            const remainingUnits = totalUnits - allocations.reduce((total, order) => total + order.baseUnits, 0);
            const remainderOperationIds = new Set(
                [...allocations]
                    .sort((left, right) => right.remainder - left.remainder || left.index - right.index)
                    .slice(0, remainingUnits)
                    .map(order => order.operationId)
            );

            return Object.fromEntries(allocations.map(order => [
                order.operationId,
                (order.baseUnits + (remainderOperationIds.has(order.operationId) ? 1 : 0)) / 100
            ]));
        },
        nestingSuggestedTotal() {
            return this.sumField("producedQuantity");
        },
        nestingAllocationMatchesTotal() {
            return Math.abs(this.nestingSuggestedTotal - Number(this.totalProduced || 0)) < 0.005;
        },
        normalReworkTotal() {
            return this.sumField("reworkQuantity");
        },
        refuseTotal() {
            return this.sumField("refuseQuantity");
        },
        hasMissingReason() {
            return this.grouping.orders.some(order => this.reasonMissing(order.operationId));
        },
        hasAnyQuantity() {
            if (this.isNesting) return Number(this.totalProduced || 0) > 0 || this.refuseTotal > 0;
            return this.normalProducedTotal > 0 || this.normalReworkTotal > 0 || this.refuseTotal > 0;
        },
        canSave() {
            return this.isRunning &&
                this.hasAnyQuantity &&
                !this.hasMissingReason &&
                Number(this.totalProduced || 0) >= 0 &&
                (!this.isNesting || (
                    Number(this.totalProduced || 0) <= this.remainingBalance &&
                    this.nestingAllocationMatchesTotal &&
                    this.grouping.orders.every(order =>
                        Number(this.forms[order.operationId]?.producedQuantity || 0) >= 0)
                ));
        },
        sapErrorDescription() {
            return this.appointmentCreated
                ? "As OPs listadas abaixo não foram apontadas. As demais foram confirmadas e registradas normalmente."
                : "O SAP recusou as OPs abaixo. Nenhum apontamento deste envio foi registrado no MySFC.";
        }
    },
    watch: {
        totalProduced() {
            this.applyNestingSuggestions();
        }
    },
    created() {
        this.load();
    },
    methods: {
        getRefuseReasons(workcenterId) {
            return this.refuseReasons.filter(r => !r.workcenterIds?.length || r.workcenterIds.includes(workcenterId));
        },
        typeLabel,
        async load() {
            this.loading = true;
            this.loadError = "";

            try {
                const api = createOrderGroupingApi(this.$http2);
                const grouping = await api.get(this.$route.params.id);
                const context = await api.appointmentContext(this.siteId, grouping.resourceId);
                this.grouping = grouping;
                this.refuseReasons = context.refuseReasons || [];
                grouping.orders.forEach(order => {
                    this.$set(this.forms, order.operationId, {
                        producedQuantity: 0,
                        reworkQuantity: 0,
                        refuseQuantity: 0,
                        refuseReasonId: null
                    });
                });
            } catch (error) {
                this.loadError = apiErrorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        sumField(field) {
            return Object.values(this.forms).reduce((total, form) => total + Number(form[field] || 0), 0);
        },
        adjustTotalProduced(quantity) {
            const currentQuantity = Number(this.totalProduced || 0);
            const adjustedQuantity = Math.min(
                Math.max(currentQuantity + quantity, 0),
                this.remainingBalance
            );
            this.totalProduced = this.roundQuantity(adjustedQuantity);
        },
        setTotalProduced() {
            this.totalProduced = this.roundQuantity(this.remainingBalance);
        },
        normalizeTotalProduced() {
            this.totalProduced = this.roundQuantity(
                Math.min(Math.max(Number(this.totalProduced || 0), 0), this.remainingBalance)
            );
        },
        normalizeSuggestedQuantity(operationId) {
            const form = this.forms[operationId];
            if (form) form.producedQuantity = this.roundQuantity(Math.max(Number(form.producedQuantity || 0), 0));
        },
        roundQuantity(value) {
            return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
        },
        applyNestingSuggestions() {
            if (!this.isNesting) return;

            Object.entries(this.nestingSuggestedProduction).forEach(([operationId, quantity]) => {
                const form = this.forms[operationId];
                if (form) form.producedQuantity = quantity;
            });
        },
        nestingAllocationWeight(order) {
            const allocationType = String(this.grouping?.allocationType || "").replace(/_/g, "");
            if (allocationType === "PERCENTAGE") return Number(order.allocationPercentage || 0);
            if (allocationType === "FIXEDQUANTITY") return Number(order.allocatedQuantity || 0);
            return Number(order.plannedQuantity || 0);
        },
        reasonMissing(operationId) {
            const form = this.forms[operationId];
            return Number(form?.refuseQuantity || 0) > 0 && !form?.refuseReasonId;
        },
        clearReasonWhenNoRefuse(operationId) {
            if (Number(this.forms[operationId].refuseQuantity || 0) <= 0)
                this.forms[operationId].refuseReasonId = null;
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
        },
        buildPayload() {
            return {
                requestId: this.appointmentRequestId,
                totalProducedQuantity: this.isNesting ? Number(this.totalProduced || 0) : null,
                eventTime: new Date().toISOString(),
                orders: this.grouping.orders.map(order => {
                    const form = this.forms[order.operationId];
                    return {
                        operationId: order.operationId,
                        producedQuantity: this.isNesting
                            ? this.roundQuantity(form.producedQuantity)
                            : Number(form.producedQuantity || 0),
                        reworkQuantity: this.isNesting ? 0 : Number(form.reworkQuantity || 0),
                        refuseQuantity: Number(form.refuseQuantity || 0),
                        refuseReasonId: Number(form.refuseQuantity || 0) > 0 ? form.refuseReasonId : null
                    };
                })
            };
        },
        async save() {
            if (this.saving) return;
            if (this.isNesting) {
                this.normalizeTotalProduced();
                this.grouping.orders.forEach(order => this.normalizeSuggestedQuantity(order.operationId));
            }
            if (!this.canSave) return;

            this.appointmentRequestId ||= this.createRequestId();
            this.saving = true;
            this.error = "";
            try {
                const result = await createOrderGroupingApi(this.$http2).createAppointment(
                    this.grouping.id,
                    this.buildPayload()
                );
                if (result?.sapErrors?.length) {
                    this.sapErrors = result.sapErrors;
                    this.appointmentCreated = Boolean(result.appointmentCreated);
                    this.sapErrorModalOpen = true;
                    return;
                }
                this.back();
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.saving = false;
            }
        },
        createRequestId() {
            if (window.crypto?.randomUUID) return window.crypto.randomUUID();

            const bytes = new Uint8Array(16);
            window.crypto.getRandomValues(bytes);
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const value = [...bytes].map(byte => byte.toString(16).padStart(2, "0")).join("");
            return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
        },
        closeSapErrorModal() {
            this.sapErrorModalOpen = false;
            this.appointmentRequestId = null;
            if (this.appointmentCreated) this.back();
        },
        confirmSapErrorModal() {
            this.sapErrorModalOpen = false;
            this.back();
        },
        back() {
            this.$router.push({
                name: "operator-order-grouping-detail",
                params: { prefix: this.$route.params.prefix, id: this.$route.params.id }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.grouping-appointment {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 16px;

        > div {
            flex: 1;
            min-width: 0;
        }

        h1 {
            margin: 0;
            font-family: "Raleway";
            font-size: 24px;
            font-weight: 600;
        }

        p {
            margin: 3px 0 0;
            overflow: hidden;
            color: #998f8a;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

.icon-button,
.secondary-button,
.primary-button {
    min-height: 40px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}
.icon-button { width: 40px; border: 1px solid #cfc4be; background: #fff; color: #974900; }
.secondary-button { border: 1px solid #974900; background: #fff; color: #974900; padding: 0 16px; font-weight: 600; }
.primary-button { border: 1px solid #974900; background: #974900; color: #fff; padding: 0 16px; font-weight: 600; }
.primary-button:disabled { opacity: 0.45; }

.type-pill { min-height: 23px; border-radius: 12px; padding: 3px 10px; background: #edf5ff; color: #2463a5; font-size: 11px; font-weight: 600; }
.type-pill--nesting { background: #f4edff; color: #6f42a5; }

.appointment-timers { margin-bottom: 14px; }
.running-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    border: 1px solid #e4a11b;
    border-radius: 5px;
    padding: 10px 12px;
    background: #fff8e6;
    color: #835900;
}

.sap-error-list {
    display: grid;
    gap: 8px;

    article {
        display: grid;
        gap: 4px;
        border: 1px solid #ead5d2;
        border-left: 3px solid #b42318;
        border-radius: 5px;
        padding: 10px 12px;
        background: #fffafa;
    }

    strong { color: #79231d; font-size: 12px; }
    span { color: #655d59; font-size: 12px; line-height: 1.45; overflow-wrap: anywhere; }
}

.nesting-production,
.appointment-table {
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;

    > header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        padding: 16px;
        border-bottom: 1px solid #ece0db;

        h2 { margin: 0; font-family: "Raleway"; font-size: 16px; font-weight: 600; }
        p { margin: 3px 0 0; color: #998f8a; font-size: 12px; }
        > span { border-radius: 11px; padding: 3px 10px; background: #f4edff; color: #6f42a5; font-size: 11px; font-weight: 600; }
    }
}

.nesting-production {
    margin-bottom: 14px;

    &__metrics {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        padding: 14px 16px 0;

        div {
            min-height: 72px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            border-radius: 5px;
            padding: 12px;
            background: #f7f6f5;
        }

        small { color: #998f8a; }
        strong { margin-top: 4px; font-size: 20px; }
    }
}

.total-produced-field {
    display: grid;
    grid-template-columns: minmax(210px, 1fr) minmax(0, 3fr);
    align-items: center;
    gap: 14px;
    margin: 14px 16px 16px;
    border: 1px solid #f3811a;
    border-radius: 5px;
    padding: 10px 12px;

    &__label {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 10px;
        color: #b45c0a;

        svg { width: 22px; height: 22px; flex: 0 0 auto; }
        small { color: #7e7570; }
    }
}

.production-stepper {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    button {
        min-width: 0;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #b45c0a;
        border-radius: 5px;
        background: #fff;
        color: #974900;
        font-weight: 600;

        &:disabled { opacity: 0.4; }
    }

    &__step-button svg { width: 19px; height: 19px; }
    &__shortcut-button { padding: 0 5px; font-size: 13px; }

    &__main {
        min-width: 240px;
        display: grid;
        grid-template-columns: 42px minmax(90px, 1fr) 42px;
        align-items: center;
        gap: 8px;
    }

    &__shortcuts {
        min-width: 0;
        flex: 1;
        display: grid;
        grid-template-columns: repeat(6, minmax(54px, 1fr)) minmax(180px, 1.5fr);
        gap: 8px;
    }

    &__total-button {
        gap: 7px;
        padding: 0 10px;
        background: #974900 !important;
        color: #fff !important;
        white-space: nowrap;

        svg { width: 17px; height: 17px; }
    }

    &__value {
        min-width: 0;
        border-bottom: 2px solid #cfc4be;

        input {
            width: 100%;
            height: 40px;
            border: 0;
            outline: 0;
            background: transparent;
            color: #4c4541;
            font-size: 24px;
            font-weight: 500;
            text-align: center;
            appearance: textfield;

            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button { margin: 0; appearance: none; }
        }
    }
}

.table-scroll { overflow-x: auto; }
table { width: 100%; min-width: 1020px; border-collapse: collapse; }
th, td { padding: 11px 12px; border-bottom: 1px solid #f0ecea; font-size: 12px; text-align: left; vertical-align: top; }
th { background: #fbfaf9; color: #7e7570; font-size: 10px; text-transform: uppercase; }
tfoot td { background: #f7f6f5; vertical-align: middle; }
.numeric { text-align: right; }
.description-cell { max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mobile-suggested-quantity { display: none; margin-top: 3px; color: #974900; font-weight: 600; white-space: nowrap; }
.quantity-input--suggested { border-color: #f3811a; color: #974900; font-weight: 600; }
.allocation-mismatch { color: #b42318; }

.quantity-input,
.reason-select {
    height: 36px;
    border: 1px solid #cfc4be;
    border-radius: 5px;
    background: #fff;
    color: #4c4541;
}
.quantity-input { width: 92px; padding: 0 8px; text-align: right; }
.reason-select { width: 100%; min-width: 200px; padding: 0 8px; }
.reason-select:disabled { background: #f3f1f0; color: #998f8a; }
.reason-select.invalid { border-color: #d92d20; color: #b42318; }
.reason-error { display: block; margin-top: 3px; color: #b42318; }

.appointment-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 9px;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #ece0db;
}

.form-error {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 7px;
    color: #b42318;
}

.state-message { min-height: 260px; display: flex; align-items: center; justify-content: center; gap: 9px; color: #998f8a; }
.state-message--error { color: #b42318; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1180px) {
    .total-produced-field { grid-template-columns: 1fr; }
    .production-stepper { align-items: stretch; flex-direction: column; }
    .production-stepper__main { width: 100%; }
    .production-stepper__shortcuts { grid-template-columns: repeat(6, minmax(0, 1fr)); }
}

@media (max-width: 1024px) {
    .production-stepper__shortcuts { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .production-stepper__total-button { grid-column: 1 / -1; }

    .appointment-table > header {
        align-items: flex-start;

        > span {
            max-width: 45%;
            overflow-wrap: anywhere;
            text-align: right;
            white-space: normal;
        }
    }

    .table-scroll {
        overflow: visible;
        padding: 12px;
        background: #f7f6f5;
    }

    table {
        min-width: 0;
        display: block;
    }

    thead { display: none; }

    tbody {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
    }

    tbody tr {
        min-width: 0;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        align-content: start;
        border: 1px solid #ded8d4;
        border-radius: 5px;
        background: #fff;
        overflow: hidden;
    }

    tbody td {
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
        padding: 10px 12px;
        border-bottom: 1px solid #f0ecea;
        text-align: left;

        &::before {
            content: attr(data-label);
            color: #998f8a;
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
        }
    }

    tbody .description-cell,
    tbody .reason-cell {
        grid-column: 1 / -1;
    }

    tbody .description-cell {
        max-width: none;
        white-space: normal;
    }

    tbody .quantity-input,
    tbody .reason-select {
        width: 100%;
        min-width: 0;
    }

    tbody .mobile-suggested-quantity { display: none; }

    tfoot {
        display: block;
        margin-top: 12px;
        border: 1px solid #ded8d4;
        border-radius: 5px;
        background: #fff;
        overflow: hidden;
    }

    tfoot tr {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }

    tfoot td {
        min-width: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
        border-bottom: 0;
        background: #fff;
        text-align: left;

        &::before {
            content: attr(data-label);
            color: #998f8a;
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
        }
    }

    tfoot .totals-title {
        grid-column: 1 / -1;
        border-bottom: 1px solid #ece0db;
        background: #fbfaf9;

        &::before { content: none; }
    }
}

@media (max-width: 800px) {
    .nesting-production__metrics { grid-template-columns: repeat(2, 1fr); }
    .appointment-footer { align-items: stretch; }
    .appointment-footer button { width: 100%; }
}

@media (max-width: 700px) {
    tbody { grid-template-columns: 1fr; }
}

@media (max-width: 520px) {
    .grouping-appointment__header h1 { font-size: 20px; }
    .nesting-production > header,
    .appointment-table > header { align-items: flex-start; flex-direction: column; }
    .nesting-production__metrics { grid-template-columns: 1fr; }
    .total-produced-field { margin-right: 10px; margin-left: 10px; }
    .production-stepper__main { min-width: 0; grid-template-columns: 42px minmax(0, 1fr) 42px; }
    .production-stepper__shortcuts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .production-stepper__total-button { grid-column: 1 / -1; }
}
</style>
